import {
  IcoChecklist16,
  IcoCode16,
  IcoMinusBig16,
  IcoOrderedList16,
  IcoQuoteLeft16,
  IcoTable16,
  IcoText16,
  IcoUnorderedList16,
} from '@onefootprint/icons';
import { type Editor, Extension, type Range } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { ReactRenderer } from '@tiptap/react';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import tippy, { type Instance } from 'tippy.js';

export type SlashCommandItem = {
  title: string;
  description: string;
  icon: ReactNode;
  /** Items with no section lead the menu, unlabelled. */
  section?: string;
  command: (props: { editor: Editor; range: Range }) => void;
};

type CommandItem = SlashCommandItem;

const BASIC_BLOCKS = 'Basic blocks';

const BUILT_INS: Omit<CommandItem, 'section'>[] = [
  {
    title: 'Text',
    description: 'Plain text paragraph',
    icon: <IcoText16 />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setParagraph().run();
    },
  },
  {
    title: 'Heading 1',
    description: 'Large section heading',
    icon: <span className="slash-menu-text-icon">H1</span>,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHeading({ level: 1 }).run();
    },
  },
  {
    title: 'Heading 2',
    description: 'Medium section heading',
    icon: <span className="slash-menu-text-icon">H2</span>,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHeading({ level: 2 }).run();
    },
  },
  {
    title: 'Heading 3',
    description: 'Small section heading',
    icon: <span className="slash-menu-text-icon">H3</span>,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHeading({ level: 3 }).run();
    },
  },
  {
    title: 'Bulleted list',
    description: 'Unordered list',
    icon: <IcoUnorderedList16 />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: 'Numbered list',
    description: 'Ordered list',
    icon: <IcoOrderedList16 />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    title: 'To-do list',
    description: 'Checklist with checkboxes',
    icon: <IcoChecklist16 />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleTaskList().run();
    },
  },
  {
    title: 'Quote',
    description: 'Blockquote',
    icon: <IcoQuoteLeft16 />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setBlockquote().run();
    },
  },
  {
    title: 'Divider',
    description: 'Horizontal line separator',
    icon: <IcoMinusBig16 />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHorizontalRule().run();
    },
  },
  {
    title: 'Code block',
    description: 'Code snippet',
    icon: <IcoCode16 />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setCodeBlock().run();
    },
  },
  {
    title: 'Table',
    description: 'Insert a table',
    icon: <IcoTable16 />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    },
  },
];

const COMMANDS: CommandItem[] = BUILT_INS.map(item => ({ ...item, section: BASIC_BLOCKS }));

type CommandListHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

/** Entries keep the flat item index, which is what keyboard nav selects by. */
type CommandGroup = { section?: string; entries: { item: CommandItem; index: number }[] };

const groupBySection = (items: CommandItem[]): CommandGroup[] =>
  items.reduce<CommandGroup[]>((groups, item, index) => {
    const open = groups.at(-1);
    if (open && open.section === item.section) open.entries.push({ item, index });
    else groups.push({ section: item.section, entries: [{ item, index }] });
    return groups;
  }, []);

const CommandList = forwardRef<CommandListHandle, { items: CommandItem[]; command: (item: CommandItem) => void }>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const isKeyboardNav = useRef(false);

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index];
        if (item) command(item);
      },
      [items, command],
    );

    useEffect(() => {
      setSelectedIndex(-1);
    }, [items]);

    // Only scroll into view on keyboard navigation, not mouse hover
    useEffect(() => {
      if (!isKeyboardNav.current) return;
      isKeyboardNav.current = false;
      const el = containerRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'ArrowUp') {
          isKeyboardNav.current = true;
          setSelectedIndex(prev => (prev <= 0 ? items.length - 1 : prev - 1));
          return true;
        }
        if (event.key === 'ArrowDown') {
          isKeyboardNav.current = true;
          setSelectedIndex(prev => (prev < 0 ? 0 : (prev + 1) % items.length));
          return true;
        }
        if (event.key === 'Enter') {
          if (selectedIndex >= 0) selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="slash-menu">
          <div className="slash-menu-empty">No results</div>
        </div>
      );
    }

    return (
      <div ref={containerRef} className="slash-menu">
        {groupBySection(items).map(group => (
          <div key={group.section ?? ''} className="slash-menu-group">
            {group.section && <div className="slash-menu-section">{group.section}</div>}
            {group.entries.map(({ item, index }) => (
              <button
                key={item.title}
                type="button"
                data-index={index}
                className={`slash-menu-item ${index === selectedIndex ? 'is-selected' : ''}`}
                onClick={() => selectItem(index)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <span className="slash-menu-icon">{item.icon}</span>
                <div className="slash-menu-text">
                  <span className="slash-menu-title">{item.title}</span>
                  <span className="slash-menu-description">{item.description}</span>
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  },
);

const renderItems: SuggestionOptions['render'] = () => {
  let component: ReactRenderer;
  let popup: Instance[];
  let releasePage: (() => void) | null = null;

  // The menu is anchored to a caret, so a page that moves under it pulls the two apart.
  // Wheel/touch are cancelled unless the gesture is over the menu (its own list still
  // scrolls). Capture-phase scroll is the backstop for programmatic scrollIntoView —
  // Popper only watches body's scroll parents, not an overflow-y-auto editor column.
  const holdPageStill = () => {
    const isOverMenu = (target: EventTarget | null) => target instanceof Node && !!popup?.[0]?.popper?.contains(target);
    const blockScroll = (event: Event) => {
      if (!isOverMenu(event.target)) event.preventDefault();
    };
    const reposition = () => popup?.[0]?.popperInstance?.update();

    document.addEventListener('wheel', blockScroll, { capture: true, passive: false });
    document.addEventListener('touchmove', blockScroll, { capture: true, passive: false });
    document.addEventListener('scroll', reposition, true);
    releasePage = () => {
      document.removeEventListener('wheel', blockScroll, true);
      document.removeEventListener('touchmove', blockScroll, true);
      document.removeEventListener('scroll', reposition, true);
    };
  };

  return {
    onStart: props => {
      component = new ReactRenderer(CommandList, {
        props,
        editor: props.editor,
      });

      if (!props.clientRect) {
        return;
      }

      popup = tippy('body', {
        getReferenceClientRect: props.clientRect as () => DOMRect,
        appendTo: () => document.body,
        content: component.element,
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
      });
      holdPageStill();
    },
    onUpdate: props => {
      component?.updateProps(props);

      if (!props.clientRect) {
        return;
      }

      popup?.[0].setProps({
        getReferenceClientRect: props.clientRect as () => DOMRect,
      });
    },
    onKeyDown: props => {
      if (props.event.key === 'Escape') {
        popup?.[0].hide();
        return true;
      }
      return (component?.ref as CommandListHandle | null)?.onKeyDown(props.event) ?? false;
    },
    onExit: () => {
      releasePage?.();
      releasePage = null;
      popup?.[0].destroy();
      component?.destroy();
    },
  };
};

export const slashCommandPluginKey = new PluginKey('slashCommand');

export const SlashCommand = Extension.create<{
  suggestion: Partial<SuggestionOptions>;
  getExtraCommands: () => SlashCommandItem[];
}>({
  name: 'slashCommand',

  addOptions() {
    return {
      getExtraCommands: () => [],
      suggestion: {
        char: '/',
        pluginKey: slashCommandPluginKey,
        decorationTag: 'span',
        decorationClass: 'slash-command-pill',
        decorationContent: 'Type to search',
        items: ({ query, editor }: { query: string; editor: Editor }) => {
          const options = editor.extensionManager.extensions.find(item => item.name === 'slashCommand')?.options;
          const extra = (options?.getExtraCommands?.() ?? []) as SlashCommandItem[];
          const overridden = new Set(extra.map(item => item.title));
          const available = [...extra, ...COMMANDS.filter(item => !overridden.has(item.title))];
          return available.filter(item => item.title.toLowerCase().includes(query.toLowerCase()));
        },
        command: ({ editor, range, props }: { editor: Editor; range: Range; props: CommandItem }) => {
          props.command({ editor, range });
        },
        render: renderItems,
      } satisfies Partial<SuggestionOptions>,
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
